import { forwardRef, useCallback } from "react"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

import { useImageInlineToggle } from "@/tiptap/ui/image-inline-toggle-button"

import { Button } from "@/tiptap/ui-primitive/button"

/**
 * Switches the selected image between the block `image` node and the
 * `inlineImage` atom without re-uploading.
 *
 * For custom button implementations, use the `useImageInlineToggle` hook instead.
 */
export const ImageInlineToggleButton = forwardRef((
  {
    editor: providedEditor,
    text,
    hideWhenUnavailable = false,
    onToggled,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const {
    isVisible,
    canToggle,
    mode,
    handleToggle,
    label,
    Icon,
  } = useImageInlineToggle({
    editor,
    hideWhenUnavailable,
    onToggled,
  })

  const handleClick = useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleToggle()
  }, [handleToggle, onClick])

  if (!isVisible) {
    return null
  }

  return (
    <Button
      type="button"
      variant="ghost"
      data-active-state="off"
      data-image-mode={mode || undefined}
      role="button"
      tabIndex={-1}
      disabled={!canToggle}
      data-disabled={!canToggle}
      aria-label={label}
      tooltip={label}
      onClick={handleClick}
      {...buttonProps}
      ref={ref}>
      {children ?? (
        <>
          <Icon className="tiptap-button-icon" />
          {text && <span className="tiptap-button-text">{text}</span>}
        </>
      )}
    </Button>
  );
})

ImageInlineToggleButton.displayName = "ImageInlineToggleButton"
